import {
  fields,
  type Mapping,
  type ParsedFile,
  type InputRow,
  type MatchedRow,
} from "./types";

export const validMapping = (mapping: Mapping): boolean => {
  const indices = Object.values(mapping).filter(
    (value): value is number => value !== null && value !== undefined
  );
  return (
    mapping.companyName !== null &&
    mapping.companyName !== undefined &&
    new Set(indices).size === indices.length
  );
};

/**
 * 同じ項目に対応する列が複数ある場合は推測で選ばず、列番号を指定してもらう
 *
 * @example
 * 列番号が0始まりで、見出しが ["会社名", "法人番号"] の場合
 * → { companyName: 0, corporateNumber: 1 }
 * 見出しが ["会社名", "法人名"] の場合
 * → {}（どちらもcompanyNameの候補になるため、自動選択しない）
 */
export const detectMapping = (file: ParsedFile): Mapping => {
  const names = {
    companyName: ["法人名", "会社名", "商号", "商号又は名称"],
    prefectureName: ["都道府県", "都道府県名"],
    cityName: ["市区町村", "市区町村名"],
    townName: ["町名"],
    corporateNumber: ["法人番号"],
    corporateIdentificationNumber: ["会社法人等番号"],
  };
  const mapping: Mapping = {};
  fields.forEach((field) => {
    const matches = file.columns.filter((column) =>
      names[field].includes(column.header.trim())
    );
    if (matches.length === 1) mapping[field] = matches[0].index;
  });
  return mapping;
};

export const mapRows = (file: ParsedFile, mapping: Mapping): InputRow[] =>
  file.rows.map((row) => {
    const result: InputRow = {
      rowNumber: row.rowNumber,
      companyName: "",
      prefectureName: "",
      cityName: "",
      townName: "",
      corporateNumber: "",
      corporateIdentificationNumber: "",
      resolutionSource: "AUTO",
      explicitlyExcluded: false,
      cellErrors: {},
    };
    fields.forEach((field) => {
      const index = mapping[field];
      if (index === null || index === undefined) return;
      const cell = row.cells[index];
      result[field] = cell?.value ?? "";
      if (cell?.errorCode) result.cellErrors[field] = cell.errorCode;
    });
    return result;
  });

/** 部分再突合でも、既存の確定番号との重複を取得対象に含めない */
export const markDuplicates = (rows: MatchedRow[]): MatchedRow[] => {
  const first = new Map<string, number>();
  return [...rows]
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .map((row) => {
      if (row.targetState !== "TARGET" || !row.corporation) return row;
      const number = row.corporation.corporateIdentificationNumber;
      const previous = first.get(number);
      if (previous !== undefined)
        return {
          ...row,
          status: "DUPLICATE",
          targetState: "EXCLUDED",
          duplicateOfRowNumber: previous,
          reasonCodes: ["DUPLICATE_IDENTIFICATION_NUMBER"],
        };
      first.set(number, row.rowNumber);
      return row;
    });
};

export const summarize = (rows: MatchedRow[]): Array<[string, number]> => [
  ["取込", rows.length],
  ["取得対象", rows.filter((row) => row.targetState === "TARGET").length],
  ["番号付与済み", rows.filter((row) => row.corporation).length],
  [
    "要確認",
    rows.filter((row) => ["NEEDS_REVIEW", "UNMATCHED"].includes(row.status))
      .length,
  ],
  ["エラー", rows.filter((row) => row.status === "ERROR").length],
  ["重複", rows.filter((row) => row.status === "DUPLICATE").length],
];
